import { useDispatch, useSelector } from 'react-redux';
import upperCase from 'lodash/upperCase';
import UserAccount, { IUserAccount } from '../models/UserAccount';
import { getInsurerFromState, getUserAccount, getUserProfile, isAuthenticated, SIGN_OUT, UPDATE_ACCOUNT, UPDATE_INSURER, UPDATE_PROFILE, USER_ID } from '../states/slices/userAccountSlice';
import { SIGN_IN } from '../resources/links';
import { IUser, User } from '../models/User';
import { useNavigate } from 'react-router-dom';
import { Insurer } from '../models/insurer/Insurer';
import { IInsurer } from '../models/insurer/IInsurer';
import { SET_TERMS_AND_CONDITIONS_DISMISSED, REDIRECT_URL, RESET_GLOBALS } from '../states/slices/globalSlice';
import { getPathAndParamsStringFromUrl } from '../utils/miscFunctions';
import { signoutUser } from '../api/requests';
import { persistor } from '../states';
import { useQueryClient } from '@tanstack/react-query';

function useUserAccount() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const PROFILE = useSelector(getUserProfile);
  const ACCOUNT = useSelector(getUserAccount);
  const IS_AUTHENTICATED = useSelector(isAuthenticated);
  const INSURER = useSelector(getInsurerFromState);
  const nameInitials = upperCase(PROFILE?.firstName?.charAt(0) + PROFILE?.lastName?.charAt(0)).trim();

  const navigate = useNavigate();

  const setProfile = (data: IUser): IUser => {
    dispatch(UPDATE_PROFILE(data));
    // Persist immediately too: many routes are conditionally registered on the
    // profile (e.g. isInternalAdmin()), so if the profile isn't persisted by the
    // time a deep-link is hard-reloaded, react-router can't match the gated route
    // and falls through to the dashboard. flush() writes the full store (token +
    // profile) synchronously. See the matching flush in setAccount.
    persistor.flush().catch(() => { /* best-effort; throttled write still covers it */ });
    return PROFILE;
  };

  const updateProfile = (data: Partial<IUser>): IUser => {
    dispatch(UPDATE_PROFILE(data));
    return PROFILE;
  };

  const getProfile = (): IUser => {
    return PROFILE;
  };

  const getUser = (): User => {
    return new User({
      ...PROFILE
    });
  };

  const setAccount = (data: IUserAccount): IUserAccount => {
    dispatch(UPDATE_ACCOUNT(data));
    // Force-persist the auth token immediately. redux-persist's throttled
    // background write can lag several seconds after login, so a hard reload
    // or deep-link opened in that window finds nothing persisted and
    // PrivateRoute bounces the (actually logged-in) user to /auth. flush()
    // writes synchronously so the session survives an immediate refresh.
    persistor.flush().catch(() => { /* best-effort; throttled write still covers it */ });
    return ACCOUNT;
  };

  const updateAccount = (data: Partial<IUserAccount>): IUserAccount => {
    dispatch(UPDATE_ACCOUNT(data));
    return ACCOUNT;
  };

  const updateInsurerInState = (data: Partial<IInsurer>): IInsurer => {
    dispatch(UPDATE_INSURER(data));
    return INSURER;
  };

  const getAccount = () => {
    return new UserAccount(ACCOUNT);
  };

  const getInsurer = (): Insurer => {
    return new Insurer({
      ...INSURER
    });
  };

  const signOut = async (redirect = true) => {
    // Capture current state before any operations
    const redirectUrl = getPathAndParamsStringFromUrl(window.location.toString()) ?? '';
    const accessRight = ACCOUNT.accessRight;
    
    // Create a cleanup function to ensure consistent state
    const cleanupClientState = async () => {
      // Dispatch all state resets atomically
      dispatch(SIGN_OUT());
      dispatch(RESET_GLOBALS());
      dispatch(REDIRECT_URL(redirectUrl));
      dispatch(SET_TERMS_AND_CONDITIONS_DISMISSED(false));

      // Wipe the react-query cache. It's a module-level singleton that only
      // resets on a full page reload, so without this the NEXT account to log
      // in (SPA navigation, no refresh) sees the PREVIOUS account's cached
      // server data on the dashboard until staleTime (5 min) elapses. A manual
      // refresh "fixing it" was the tell — that just rebuilt the cache.
      queryClient.clear();

      // Force-persist the now-signed-out state synchronously. redux-persist's
      // write is throttled (1s), so without this an immediate refresh after
      // logout rehydrates the STILL-authenticated state from localStorage and
      // the user lands back in as logged in. Mirrors the flush() in setAccount.
      await persistor.flush();
    };

    try {
      // Step 1: Call server-side signout first
      if (accessRight) {
        await signoutUser(accessRight);
      }
      
      // Step 2: Only after successful server signout, clean up client state
      await cleanupClientState();

      // Step 3: Navigate after state is fully cleaned up
      if (redirect) {
        setTimeout(() => {
          navigate(SIGN_IN, { replace: true });
        }, 0);
      }
    } catch (error) {
      console.error('Signout error:', error);
      
      // Additional cleanup for error scenario. Dispatch BEFORE the cleanup so
      // this reset is included in the synchronous persistor.flush() below.
      dispatch(USER_ID(''));

      // Even on error, clean up client state to ensure user is logged out locally
      await cleanupClientState();

      // Navigate after error cleanup
      if (redirect) {
        setTimeout(() => {
          navigate(SIGN_IN, { replace: true });
        }, 0);
      }
    }
  };

  const user = new User(getProfile());

  return {
    setProfile,
    getProfile,
    getUser,
    updateProfile,
    signOut,
    getAccount,
    updateAccount,
    setAccount,
    IS_AUTHENTICATED,
    nameInitials,
    updateInsurerInState,
    getInsurer,
    user
  };
}

export default useUserAccount;
